import { useEffect, useRef } from 'react'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeVideoWall,
  selectVideoWallIsOpen,
  toggleVideoWall,
} from '@/redux/videoWall/videoWallSlice'

import { VIDEO_WALL_SHORTCUT_KEY } from '../constants/constants'

// Elements where a bare letter keypress means "type a character", not "trigger a
// global shortcut".
const isEditableTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null
  if (!element) {
    return false
  }
  const { tagName } = element
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    element.isContentEditable
  )
}

/**
 * Binds the global keyboard shortcut for the video wall and exposes its open
 * state (from redux). Pressing "W" anywhere in the product (outside of text
 * inputs) toggles the overlay; Escape closes it.
 */
export const useVideoWallShortcut = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectVideoWallIsOpen)

  // Read the latest open-state inside the listener without re-binding it.
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore auto-repeat events while a key is held down.
      if (event.repeat) {
        return
      }
      // Never hijack browser/OS chords such as Cmd+W or Ctrl+W.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      // Some synthetic/IME/autofill key events arrive with no `key`; nothing to
      // match, and reading `.toLowerCase()` off it below would throw.
      if (event.key == null) {
        return
      }

      if (event.key === 'Escape') {
        // When a field/dropdown is focused (e.g. the camera picker's react-select
        // input), Escape belongs to that control — closing its menu — not to the
        // wall. Let it through without also closing the overlay.
        if (isOpenRef.current && !isEditableTarget(event.target)) {
          dispatch(closeVideoWall())
        }
        return
      }

      if (event.key.toLowerCase() !== VIDEO_WALL_SHORTCUT_KEY) {
        return
      }
      // Don't fire while the user is typing in a field.
      if (isEditableTarget(event.target)) {
        return
      }

      event.preventDefault()
      dispatch(toggleVideoWall())
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [dispatch])

  return { isOpen }
}
